import { useLocation, matchPath, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, List, ListItemIcon, ListItemText, Collapse, ListItemButton, useTheme, useMediaQuery } from '@mui/material';
import { ChevronRight, ExpandMore, FiberManualRecord, Launch } from '@mui/icons-material';
import { SysProduct } from 'src/shared/models/user';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { listMenuStyles } from 'src/shared/components/navigations/side-menu/sideMenu.styles';
import { isFeatureEnabled } from 'src/shared/functions/isFeatureEnabled';
import { useCallback } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { SideMenuDrawer } from './sideMenuDrawer';
import { SideMenuLink } from './sideMenuLink';
import { SideMenuLoading } from './sideMenuLoading';
import { SideMenuTooltip } from './sideMenuTooltip';
import { Colors } from '../../styles/colors';
import { useSideMenuUser } from './useSideMenuUser';

export const LocationMatchesArray = (array: Array<string>) => {
  const location = useLocation();
  let result = false;
  array.forEach((x) => {
    const match = matchPath({ path: `${x}/*` }, location.pathname);
    if (match) result = true;
  });
  return result;
};

const SideMenuUser = () => {
  const { sideMenuOpen, setSideMenuOpen, currentUser } = useGlobals();
  const { t, ready } = useTranslation(['common']);
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));

  const sysProducts = getSysProducts(currentUser);

  const hasCustomer = () => {
    return !!(currentUser && currentUser.customer);
  };

  const sideMenuUserItems = useSideMenuUser();

  ConsoleHelper.log('currentUser', currentUser, hasCustomer());

  if (isFeatureEnabled()) {
    sysProducts.push({
      product_id: 0,
      name: 'file',
      yvName: 'file',
    } as SysProduct);
  }

  // const locationMatches = (x: string) => {
  //   const match = matchPath({ path: `${x}` }, location.pathname);
  //   if (match) return true;
  //   return false;
  // };

  const closeMenuOnMobileDevices = useCallback(
    (isParent, hasChildren) => {
      const closeOnClick = (isParent && !hasChildren) || !isParent;
      if (isMobileDevice && closeOnClick) {
        setSideMenuOpen(false);
      }
    },
    [isMobileDevice, setSideMenuOpen],
  );

  return (
    <SideMenuDrawer>
      <List component='nav' id='sidemenu' sx={listMenuStyles}>
        {ready && currentUser ? (
          <>
            {/*             {showInProduction() && (
              <ListItem>
                <Typography sx={{ fontWeight: '300 !important', fontSize: '0.8rem !important' }}>{location.pathname}</Typography>
              </ListItem>
            )} */}
            {sideMenuUserItems.map((item) => {
              const isMenuItemDisabled = !item.showMenuItem || !sysProducts.some((x) => x.yvName.toLowerCase() === item.yvName.toLowerCase());
              const isSupport = item.yvName.toLowerCase() === 'support' && !sysProducts.some((x) => x.yvName.toLowerCase() === 'support');
              const isActive = item.subMenuItems
                ? LocationMatchesArray([item.link.toString()].concat(item.subMenuItems.map((a) => a.link.toString())))
                : false;

              if (isMenuItemDisabled && process.env.REACT_APP_HIDE_DISABLED_MENUITEMS === 'true') return null;

              return !isSupport ? (
                <SideMenuTooltip
                  showTooltip={isMenuItemDisabled || !sideMenuOpen}
                  key={item.key}
                  label={item.label}
                  isMenuItemDisabled={isMenuItemDisabled}
                >
                  <ListItemButton
                    key={item.key}
                    component={SideMenuLink as any}
                    onClick={() => closeMenuOnMobileDevices(true, !!item.subMenuItems)}
                    to={item.link}
                    disabled={isMenuItemDisabled}
                    aria-hidden={isMenuItemDisabled}
                    aria-label={t(item.label)}
                    sx={{ borderRadius: 1 }}
                    className={item.subMenuItems && isActive ? 'active' : undefined}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: Colors.LinkColor }} aria-hidden>
                      {item.Icon}
                    </ListItemIcon>
                    <ListItemText primary={t(item.label)} sx={{ ml: sideMenuOpen ? 0 : 1 }} />
                    {item.subMenuItems?.some((sub) => sub.badgeCount) && (
                      <Badge
                        variant='dot'
                        color='error'
                        sx={{
                          mr: 1,
                          '& .MuiBadge-badge': {
                            position: 'relative',
                            transform: 'none',
                            width: 8,
                            height: 8,
                            minWidth: 8,
                            borderRadius: '50%',
                            animation: 'badgePulse 2s ease-in-out infinite',
                          },
                          '@keyframes badgePulse': {
                            '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                            '50%': { opacity: 0.7, boxShadow: '0 0 0 4px rgba(211, 47, 47, 0)' },
                          },
                        }}
                      />
                    )}
                    {item.subMenuItems && <>{isActive ? <ExpandMore /> : <ChevronRight />}</>}
                  </ListItemButton>
                  {item.subMenuItems && (
                    <Collapse in={isActive} timeout='auto' unmountOnExit>
                      <List
                        component='div'
                        disablePadding
                        dense
                        sx={{
                          mt: 0.5,
                          mb: 1,
                          '& .subMenuText': {
                            fontWeight: 300,
                          },
                          '& .dotIndicator': {
                            opacity: 0.6,
                          },
                          '& .subMenuItem.active': {
                            '& .subMenuText': {
                              fontWeight: 600,
                              opacity: 1,
                            },
                            '& .dotIndicator': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {item.subMenuItems
                          .filter((x) => x.showMenuItem)
                          .map((subItem) => {
                            const isSubMenuItemDisabled = !getPermission(currentUser, subItem.permission ?? '');
                            const hideDisabledMenuItems = process.env.REACT_APP_HIDE_DISABLED_MENUITEMS === 'true';

                            if ((isMenuItemDisabled && hideDisabledMenuItems) || (isSubMenuItemDisabled && subItem.hideIfNoPermission)) return null;
                            return (
                              <SideMenuTooltip
                                showTooltip={isSubMenuItemDisabled}
                                key={subItem.key}
                                label={subItem.label}
                                isMenuItemDisabled={isSubMenuItemDisabled}
                              >
                                <ListItemButton
                                  key={subItem.key}
                                  disabled={isSubMenuItemDisabled}
                                  to={subItem.link}
                                  onClick={() => closeMenuOnMobileDevices(false, false)}
                                  className='subMenuItem'
                                  component={SideMenuLink as any}
                                >
                                  <ListItemIcon sx={{ minWidth: sideMenuOpen ? 35 : 15 }} aria-hidden className='dotIndicator'>
                                    <FiberManualRecord sx={{ fontSize: '0.6rem' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={sideMenuOpen ? t(subItem.label) : t(subItem.label).substring(0, 1)}
                                    className='subMenuText'
                                  />
                                  {subItem.badgeCount ? (
                                    <Badge
                                      badgeContent={subItem.badgeCount}
                                      color='error'
                                      sx={{
                                        ml: 'auto',
                                        mr: 1.5,
                                        '& .MuiBadge-badge': {
                                          position: 'relative',
                                          transform: 'none',
                                          minWidth: 16,
                                          height: 16,
                                          fontSize: '0.65rem',
                                          lineHeight: '16px',
                                          padding: '0 4px',
                                        },
                                      }}
                                    />
                                  ) : null}
                                </ListItemButton>
                              </SideMenuTooltip>
                            );
                          })}
                      </List>
                    </Collapse>
                  )}
                </SideMenuTooltip>
              ) : null;
            })}
          </>
        ) : (
          <SideMenuLoading />
        )}

        <Link
          to='https://hjalp.foreningssupport.se/'
          target='_blank'
          rel='noopener'
          style={{ display: 'flex', alignItems: 'center', padding: '15px', marginTop: '20px', borderTop: '1px solid #ddd' }}
        >
          <Launch fontSize='small' sx={{ minWidth: 20, marginRight: 2, color: Colors.MenuIcon }} />
          {t('common:helpLinkText')}
        </Link>
      </List>
    </SideMenuDrawer>
  );
};

export default SideMenuUser;
